import { storageHelper } from '@offer-catalog/logic'
import { initI18n } from '@offer-catalog/services/i18n'
import { AuthProvider } from '@offer/auth-lib'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { NuqsAdapter } from 'nuqs/adapters/react'
import React, { Suspense } from 'react'
import * as ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { App } from '@/components/App'
import { Loading } from '@/components/loading'
import { AriaHiddenProvider } from '@/contexts/accessibility/AriaHiddenProvider'
import { RolesProvider } from '@/contexts/roles/RolesProvider'
import { ScrollProvider } from '@/contexts/scroll/ScrollProvider'
import { ToasterProvider } from '@/contexts/toaster/ToasterProvider'
import { environment } from '@/environments/environment'
import { initDatadogRum } from '@/observability/datadogRum'
import { initAnalytics } from '@/services/analytics'

import '@/assets/styles/index.scss'

initDatadogRum({
  applicationId: environment.datadogApplicationId,
  clientToken: environment.datadogClientToken,
  env: environment.datadogEnv,
  service: environment.datadogServiceName,
  site: environment.datadogSite,
  version: environment.datadogVersion,
  sessionReplaySampleRate: environment.datadogSessionReplaySampleRate,
})

initAnalytics()

async function bootstrap() {
  const defaultLanguage = 'en-GB'
  const storedLanguage = storageHelper.getItem<string>('cat_loc')
  const initialLanguage = storedLanguage === 'fr-FR' ? storedLanguage : defaultLanguage
  await initI18n(initialLanguage)

  if (!storageHelper.getItem('theme'))
    storageHelper.setItem('theme', 'light')

  const container = document.getElementById('root')

  if (container) {
    const root = ReactDOM.createRoot(container)
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    })
    root.render(
      <React.StrictMode>
        <Suspense fallback={<Loading />}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <ScrollProvider>
                <NuqsAdapter>
                  <AuthProvider bffBaseUrl={import.meta.env.VITE_BFF_BASE_URL ?? '/bff'}>
                    <RolesProvider>
                      <AriaHiddenProvider>
                        <ToasterProvider>
                          <App />
                        </ToasterProvider>
                      </AriaHiddenProvider>
                    </RolesProvider>
                  </AuthProvider>
                </NuqsAdapter>
              </ScrollProvider>
            </BrowserRouter>
            {!environment.test && (
              <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-left"
              />
            )}
          </QueryClientProvider>
        </Suspense>
      </React.StrictMode>,
    )
  }
}

if (['msw_mocked'].includes(environment.env)) {
  import('~/__mocks__/browser')
    .then(({ worker }) => worker.start({ onUnhandledFrame: 'bypass' }))
    .then(bootstrap)
    .catch(error => console.error(error))
}
else {
  bootstrap().catch(error => console.error(error))
}
